import React, { useEffect, useRef, useState } from "react";
import { useAuth } from "./AuthContext";
import API from "./services/api";
import { buildMaintenanceAccessHeaders, hasMaintenanceLogAccessEmail } from "./utils/maintenanceAccess";
import { getUserEmail } from "./utils/userIdentity";
import "./MaintenanceLog.css";

// ─── Design tokens (runtime-only, used for dynamic/conditional colors) ────────
const C = {
  navy:    "#0B1D3A",
  navyMid: "#162D52",
  sky:     "#4A8FD4",
  skyPale: "#EBF4FD",
  ink:     "#0F1923",
  slate:   "#4A5568",
  mist:    "#8FA1B3",
  smoke:   "#E8EDF3",
  white:   "#FFFFFF",
  green:   "#2D7A4F",
  greenLt: "#D4EDE0",
  amber:   "#B45309",
  amberLt: "#FEF3C7",
  red:     "#B91C1C",
  redLt:   "#FEE2E2",
};

// ─── Sample data ──────────────────────────────────────────────────────────────
const INITIAL_UNITS = [];

const getAttachmentName = (attachment) => {
  if (!attachment) {
    return "Attachment";
  }

  if (typeof attachment === "string") {
    return attachment;
  }

  return attachment.name || attachment.originalName || attachment.fileName || "Attachment";
};

const getAttachmentId = (attachment, fallbackIndex = 0) => {
  if (!attachment) {
    return `attachment-${fallbackIndex}`;
  }

  if (typeof attachment === "string") {
    return `${attachment}-${fallbackIndex}`;
  }

  return attachment.id || attachment.attachmentId || attachment.fileId || `attachment-${fallbackIndex}`;
};

const getAttachmentDownloadUrl = (unit, attachment) => {
  if (!attachment || typeof attachment === "string") {
    return null;
  }

  return attachment.downloadUrl || (attachment.id ? `/api/maintenance-logs/${unit.id}/attachments/${attachment.id}` : null);
};

const buildEntrySnapshot = (unit) => ({
  notes: unit.notes,
  tasks: Array.isArray(unit.tasks) ? unit.tasks : [],
  attachments: Array.isArray(unit.attachments) ? unit.attachments : [],
  status: unit.status,
  completionDate: unit.completionDate,
});

const normalizeDateValue = (value) => {
  if (!value) {
    return "";
  }

  const raw = String(value).trim();
  const match = raw.match(/^(\d{4}-\d{2}-\d{2})/);
  if (match) {
    return match[1];
  }

  const parsed = new Date(raw);
  return Number.isNaN(parsed.getTime()) ? "" : parsed.toISOString().split("T")[0];
};

const getTodayDateString = () => {
  const today = new Date();
  const year = today.getFullYear();
  const month = String(today.getMonth() + 1).padStart(2, "0");
  const day = String(today.getDate()).padStart(2, "0");
  return `${year}-${month}-${day}`;
};

const isCompletionAchieved = (value) => {
  const normalizedDate = normalizeDateValue(value);
  return Boolean(normalizedDate) && normalizedDate <= getTodayDateString();
};

const isTaskCompletionAchieved = (task) => {
  const completedDate = normalizeDateValue(task?.completed);
  if (!completedDate || completedDate > getTodayDateString()) {
    return false;
  }

  return true;
};

const areAllTasksCompleted = (tasks) => {
  const normalizedTasks = Array.isArray(tasks) ? tasks : [];
  return normalizedTasks.length > 0 && normalizedTasks.every((task) => isTaskCompletionAchieved(task));
};

const deriveUnitStatus = (unit) => {
  if (areAllTasksCompleted(unit?.tasks) || isCompletionAchieved(unit?.completionDate)) {
    return "Completed";
  }

  if (unit?.status) {
    return unit.status;
  }

  if (!unit?.deficiencies || unit.deficiencies === "No active deficiencies" || unit.deficiencies === "—") {
    return "Clear";
  }

  const tasks = Array.isArray(unit?.tasks) ? unit.tasks : [];
  return tasks.length > 0 ? "In progress" : "Pending";
};

const normalizeDirectoryUser = (user) => {
  const firstName = String(user?.firstName || user?.FIRST_NAME || "").trim();
  const lastName = String(user?.lastName || user?.LAST_NAME || "").trim();
  const email = String(user?.email || user?.EMAIL || "").trim();
  const department = String(user?.department || user?.DEPARTMENT || "").trim();
  const role = String(user?.role || user?.ROLE || "").trim();
  const displayName = `${firstName} ${lastName}`.trim() || email || "Unknown employee";

  return {
    id: email || displayName,
    email,
    displayName,
    department,
    role
  };
};

const getEmployeeOptionLabel = (employee) => {
  const parts = [employee.displayName];
  if (employee.email) {
    parts.push(employee.email);
  }
  if (employee.department) {
    parts.push(employee.department);
  }
  return parts.join(" · ");
};

// ─── Tab type constants ───────────────────────────────────────────────────────
const TAB_INSPECTION  = "inspection";
const TAB_MAINTENANCE = "maintenance";
const TAB_NOTIFY      = "notify";

// ─── Icons ────────────────────────────────────────────────────────────────────
const TruckIcon = ({ color }) => (
  <svg width="22" height="16" viewBox="0 0 22 16" fill="none">
    <rect x="0"  y="5" width="14" height="9" rx="1.5" fill={color} opacity="0.25" />
    <rect x="1"  y="2" width="9"  height="5" rx="1"   fill={color} opacity="0.45" />
    <rect x="14" y="7" width="8"  height="7" rx="1.5" fill={color} opacity="0.35" />
    <circle cx="4"  cy="14" r="2" fill={color} />
    <circle cx="11" cy="14" r="2" fill={color} />
    <circle cx="18" cy="14" r="2" fill={color} />
  </svg>
);

const EquipIcon = ({ color }) => (
  <svg width="22" height="16" viewBox="0 0 22 16" fill="none">
    <rect x="3" y="6" width="16" height="8" rx="1.5" fill={color} opacity="0.25" />
    <rect x="6" y="3" width="7"  height="5" rx="1"   fill={color} opacity="0.45" />
    <circle cx="6"  cy="14" r="2" fill={color} />
    <circle cx="16" cy="14" r="2" fill={color} />
  </svg>
);

// ─── Badge ────────────────────────────────────────────────────────────────────
const Badge = ({ children, color, textColor }) => (
  <span className="badge" style={{ background: color, color: textColor }}>
    {children}
  </span>
);

const StatusPill = ({ status }) => {
  const map = {
    "Completed":   [C.greenLt, C.green],
    "In progress": [C.sky,     C.navyMid],
    "Pending":     [C.amberLt, C.amber],
    "Clear":       [C.greenLt, C.green],
  };
  const [bg, fg] = map[status] || [C.smoke, C.slate];
  return <Badge color={bg} textColor={fg}>{status}</Badge>;
};

// ─── Divider ──────────────────────────────────────────────────────────────────
const Divider = ({ label, color }) => (
  <div className="divider">
    <div className="divider-bar" style={{ background: color }} />
    <span className="divider-label">{label}</span>
    <div className="divider-line" />
  </div>
);

// ─── ReadField ────────────────────────────────────────────────────────────────
const ReadField = ({ label, value }) => (
  <div className="field">
    <div className="field-label">{label}</div>
    <div className={`field-read ${!value || value === "—" ? "empty" : ""}`}>
      {value || "—"}
    </div>
  </div>
);

// ─── EditField ────────────────────────────────────────────────────────────────
const EditField = ({ label, value, onChange, multiline, type = "text" }) => (
  <div className="field">
    <div className="field-label">{label}</div>
    {multiline ? (
      <textarea
        className="field-textarea"
        value={value}
        rows={3}
        onChange={e => onChange(e.target.value)}
      />
    ) : (
      <input
        className="field-input"
        type={type}
        value={value}
        onChange={e => onChange(e.target.value)}
      />
    )}
  </div>
);

// ─── TaskCard ─────────────────────────────────────────────────────────────────
const TaskCard = ({ task, idx, onChange, onRemove }) => (
  <div className="task-card">
    <div className="task-card-header">
      <span className="task-card-label">Task {idx + 1}</span>
      <button className="task-remove-btn" onClick={onRemove}>✕ remove</button>
    </div>
    <EditField
      label="Assigned company"
      value={task.company}
      onChange={v => onChange({ ...task, company: v })}
    />
    <EditField
      label="Task description"
      value={task.description}
      onChange={v => onChange({ ...task, description: v })}
      multiline
    />
    <div className="field-grid-2">
      <EditField
        label="Tentative completion"
        value={task.tentative}
        onChange={v => onChange({ ...task, tentative: v })}
        type="date"
      />
      <EditField
        label="Completion date"
        value={task.completed}
        onChange={v => onChange({ ...task, completed: v })}
        type="date"
      />
    </div>
  </div>
);

// ─── TabBtn ───────────────────────────────────────────────────────────────────
const TabBtn = ({ active, type, onClick, children }) => {
  const dotColors = {
    [TAB_INSPECTION]:  C.ink,
    [TAB_MAINTENANCE]: C.green,
    [TAB_NOTIFY]:      C.amber,
  };
  const dotColor = active ? C.white : dotColors[type];
  const activeClass = active
    ? type === TAB_INSPECTION  ? "active-inspection"
    : type === TAB_MAINTENANCE ? "active-maintenance"
    :                            "active-notify"
    : "";

  return (
    <button className={`tab-btn ${activeClass}`} onClick={onClick}>
      <span className="tab-dot" style={{ background: dotColor }} />
      {children}
    </button>
  );
};

// ─── InspectionTab ────────────────────────────────────────────────────────────
const InspectionTab = ({ unit, onOpenSource, sourceLoadingId }) => (
  <div className="tab-panel">
    <Divider label="Read-only — pulled from inspection form" color={C.navy} />
    <div className="date-grid">
      <ReadField label="Date"             value={unit.date} />
      <ReadField label="Vehicle / unit #" value={unit.unitId} />
    </div>
    <ReadField label="Deficiencies noted" value={unit.deficiencies} />
    <div className="field">
      <div className="field-label">Original inspection form</div>
      <button
        type="button"
        className="inspect-link"
        onClick={() => onOpenSource(unit)}
        disabled={!unit.sourceLink || sourceLoadingId === unit.id}
      >
        View inspection report <span>↗</span>
      </button>
    </div>
  </div>
);

// ─── MaintenanceTab ───────────────────────────────────────────────────────────
const MaintenanceTab = ({
  unit,
  onUpdate,
  onSave,
  onUploadAttachments,
  onRemoveAttachment,
  onOpenAttachment,
  isSaving,
  isUploadingAttachments,
  activeAttachmentId,
  hasUnsavedChanges
}) => {
  const [nextTaskId, setNextTaskId] = useState(unit.tasks.length + 1);
  const attachmentInputRef = useRef(null);

  const addTask = () => {
    onUpdate({
      ...unit,
      tasks: [...unit.tasks, { id: nextTaskId, company: "", description: "", tentative: "", completed: "" }],
    });
    setNextTaskId(n => n + 1);
  };

  const updateTask = (idx, t) =>
    onUpdate({ ...unit, tasks: unit.tasks.map((x, i) => (i === idx ? t : x)) });

  const removeTask = idx =>
    onUpdate({ ...unit, tasks: unit.tasks.filter((_, i) => i !== idx) });

  const handleAttachmentSelection = async (event) => {
    const files = Array.from(event.target.files || []);
    event.target.value = "";

    if (files.length === 0) {
      return;
    }

    await onUploadAttachments(unit, files);
  };

  return (
    <div className="tab-panel">
      <Divider label="Maintenance / repair notes — fleet manager editable" color={C.green} />
      <EditField
        label="Maintenance / repair notes"
        value={unit.notes}
        onChange={v => onUpdate({ ...unit, notes: v })}
        multiline
      />

      <div className="section-label">Assigned tasks</div>
      {unit.tasks.map((t, i) => (
        <TaskCard
          key={t.id}
          task={t}
          idx={i}
          onChange={nt => updateTask(i, nt)}
          onRemove={() => removeTask(i)}
        />
      ))}
      <button className="add-task-btn" onClick={addTask}>+ Add another task</button>

      <div className="section-label">Attachments</div>
      {unit.attachments.map((attachment, i) => {
        const attachmentId = getAttachmentId(attachment, i);
        const attachmentName = getAttachmentName(attachment);
        const canOpenAttachment = Boolean(getAttachmentDownloadUrl(unit, attachment));

        return (
        <div key={attachmentId} className="attachment-row">
          <span>📎</span>
          <button
            type="button"
            className={`attachment-name attachment-open ${canOpenAttachment ? "is-link" : ""}`}
            onClick={() => canOpenAttachment && onOpenAttachment(unit, attachment)}
            disabled={!canOpenAttachment || activeAttachmentId === attachmentId}
          >
            {activeAttachmentId === attachmentId ? "Opening..." : attachmentName}
          </button>
          <button
            type="button"
            className="attachment-remove"
            onClick={() => onRemoveAttachment(unit, attachment, i)}
            disabled={isUploadingAttachments || activeAttachmentId === attachmentId}
          >✕</button>
        </div>
        );
      })}
      <input
        ref={attachmentInputRef}
        className="attachment-input-hidden"
        type="file"
        accept=".pdf,.png,.jpg,.jpeg,application/pdf,image/png,image/jpeg"
        multiple
        onChange={handleAttachmentSelection}
      />
      <button
        type="button"
        className="add-attach-btn"
        onClick={() => attachmentInputRef.current?.click()}
        disabled={isUploadingAttachments}
      >
        {isUploadingAttachments ? "Uploading attachment..." : "+ Add attachment"}
      </button>

      <button
        type="button"
        className="add-task-btn"
        onClick={() => onSave(unit)}
        disabled={!hasUnsavedChanges || isSaving}
      >
        {isSaving ? "Saving..." : hasUnsavedChanges ? "Save maintenance changes" : "Saved"}
      </button>
    </div>
  );
};

// ─── NotifyTab ────────────────────────────────────────────────────────────────
const NotifyTab = ({ unit, onNotify, notifyingId }) => {
  return (
    <div className="tab-panel">
      <Divider label="Employee notification" color={C.amber} />
      <div className="notify-warning">
        This action can be send at any stage of maintenance, but only <strong>once</strong> per entry
      </div>
      <ReadField label="Original reporting employee" value={unit.reporter} />
      <button
        className={`notify-btn ${unit.notified ? "sent" : "unsent"}`}
        onClick={() => onNotify(unit)}
        disabled={unit.notified || notifyingId === unit.id}
      >
        {unit.notified ? "✓ Summary sent" : "Send maintenance summary to employee"}
      </button>
    </div>
  );
};

// ─── UnitRow ──────────────────────────────────────────────────────────────────
const UnitRow = ({
  unit,
  onUpdate,
  onSave,
  onUploadAttachments,
  onRemoveAttachment,
  onOpenAttachment,
  hasUnsavedChanges,
  onOpenSource,
  onNotify,
  sourceLoadingId,
  notifyingId,
  savingUnitId,
  uploadingAttachmentUnitId,
  activeAttachmentId
}) => {
  const [open, setOpen]   = useState(false);
  const [tab,  setTab]    = useState(TAB_INSPECTION);

  const isCommercial = unit.group === "commercial";
  const groupClass   = isCommercial ? "commercial" : "noncommercial";
  const openClass    = open ? "open" : "closed";

  const hasDeficiency =
    unit.deficiencies &&
    unit.deficiencies !== "—" &&
    unit.deficiencies !== "No active deficiencies";

  return (
    <div className={`unit-card ${open ? openClass : ""} ${groupClass}`}>
      {/* Header */}
      <div
        className={`unit-header ${open ? openClass : ""} ${groupClass}`}
        onClick={() => setOpen(o => !o)}
      >
        <div className={`unit-icon-wrap ${open ? "open" : "closed"}`}>
          {isCommercial
            ? <TruckIcon color={open ? C.white : C.sky}   />
            : <EquipIcon color={open ? C.white : C.green} />}
        </div>

        <div className="unit-info">
          <div className={`unit-name ${openClass}`}>{unit.label}</div>
          <div className={`unit-sub ${openClass}`}>
            {isCommercial ? "Commercial Vehicle" : "Non-Commercial Equipment"} · {unit.unitId}
          </div>
        </div>

        {hasDeficiency && (
          <Badge
            color={open ? "rgba(255,255,255,0.15)" : C.redLt}
            textColor={open ? C.white : C.red}
          >Deficiency</Badge>
        )}
        {unit.notified && (
          <Badge
            color={open ? "rgba(255,255,255,0.15)" : C.amberLt}
            textColor={open ? C.white : C.amber}
          >Notified</Badge>
        )}

        <span className={`unit-chevron ${openClass}`}>▶</span>
      </div>

      {/* Body */}
      {open && (
        <div className="unit-body">
          <div className="tab-bar">
            <TabBtn active={tab === TAB_INSPECTION}  type={TAB_INSPECTION}  onClick={() => setTab(TAB_INSPECTION)}>Inspection Data</TabBtn>
            <TabBtn active={tab === TAB_MAINTENANCE} type={TAB_MAINTENANCE} onClick={() => setTab(TAB_MAINTENANCE)}>Maintenance Notes</TabBtn>
            <TabBtn active={tab === TAB_NOTIFY}      type={TAB_NOTIFY}      onClick={() => setTab(TAB_NOTIFY)}>Notify Employee</TabBtn>
          </div>

          {tab === TAB_INSPECTION  && <InspectionTab  unit={unit} onOpenSource={onOpenSource} sourceLoadingId={sourceLoadingId} />}
          {tab === TAB_MAINTENANCE && <MaintenanceTab unit={unit} onUpdate={onUpdate} onSave={onSave} onUploadAttachments={onUploadAttachments} onRemoveAttachment={onRemoveAttachment} onOpenAttachment={onOpenAttachment} isSaving={savingUnitId === unit.id} isUploadingAttachments={uploadingAttachmentUnitId === unit.id} activeAttachmentId={activeAttachmentId} hasUnsavedChanges={hasUnsavedChanges} />}
          {tab === TAB_NOTIFY      && <NotifyTab      unit={unit} onNotify={onNotify} notifyingId={notifyingId} />}
        </div>
      )}
    </div>
  );
};

// ─── SummaryPanel ─────────────────────────────────────────────────────────────
const SummaryPanel = ({ units, onExport, isExporting }) => {
  const [sortBy,   setSortBy]   = useState("unit");
  const [selected, setSelected] = useState(() => new Set(units.map(u => u.id)));
  const [searchFilters, setSearchFilters] = useState({
    contractor: "",
    employee: "",
    unitId: "",
    dateFrom: "",
    dateTo: ""
  });

  useEffect(() => {
    setSelected(new Set(units.map(u => u.id)));
  }, [units]);

  const updateSearchFilter = (field, value) => {
    setSearchFilters((currentFilters) => ({
      ...currentFilters,
      [field]: value
    }));
  };

  const toggle = id =>
    setSelected(s => { const n = new Set(s); n.has(id) ? n.delete(id) : n.add(id); return n; });

  const getStatus = u => {
    return deriveUnitStatus(u);
  };

  const sorted = [...units].sort((a, b) => {
    if (sortBy === "unit")   return (a.unitId || "").localeCompare(b.unitId || "");
    if (sortBy === "date")   return (a.date || "").localeCompare(b.date || "");
    return getStatus(a).localeCompare(getStatus(b));
  });

  const filtered = sorted.filter((unit) => {
    const contractorFilter = searchFilters.contractor.trim().toLowerCase();
    const employeeFilter = searchFilters.employee.trim().toLowerCase();
    const unitFilter = searchFilters.unitId.trim().toLowerCase();
    const dateFromFilter = searchFilters.dateFrom.trim();
    const dateToFilter = searchFilters.dateTo.trim();

    const contractorText = (Array.isArray(unit.tasks) ? unit.tasks : [])
      .map((task) => String(task.company || "").trim().toLowerCase())
      .filter(Boolean)
      .join(" ");

    const employeeText = String(unit.reporter || "").trim().toLowerCase();
    const unitText = String(unit.unitId || "").trim().toLowerCase();
    const dateText = String(unit.date || "").trim();

    if (contractorFilter && !contractorText.includes(contractorFilter)) {
      return false;
    }

    if (employeeFilter && !employeeText.includes(employeeFilter)) {
      return false;
    }

    if (unitFilter && !unitText.includes(unitFilter)) {
      return false;
    }

    if (dateFromFilter && (!dateText || dateText < dateFromFilter)) {
      return false;
    }

    if (dateToFilter && (!dateText || dateText > dateToFilter)) {
      return false;
    }

    return true;
  });

  const filteredIds = filtered.map((unit) => unit.id);
  const hasFilteredEntries = filteredIds.length > 0;
  const areAllFilteredSelected = hasFilteredEntries && filteredIds.every((id) => selected.has(id));

  const toggleAllFiltered = () => {
    setSelected((currentSelected) => {
      const nextSelected = new Set(currentSelected);

      if (areAllFilteredSelected) {
        filteredIds.forEach((id) => nextSelected.delete(id));
      } else {
        filteredIds.forEach((id) => nextSelected.add(id));
      }

      return nextSelected;
    });
  };

  return (
    <div>
      <div className="summary-search-panel">
        <div className="summary-search-grid">
          <div className="summary-search-field">
            <label className="summary-search-label">Contractor</label>
            <input
              className="summary-search-input"
              type="text"
              value={searchFilters.contractor}
              onChange={(event) => updateSearchFilter("contractor", event.target.value)}
              placeholder="Search contractor"
            />
          </div>
          <div className="summary-search-field">
            <label className="summary-search-label">Employee</label>
            <input
              className="summary-search-input"
              type="text"
              value={searchFilters.employee}
              onChange={(event) => updateSearchFilter("employee", event.target.value)}
              placeholder="Search employee"
            />
          </div>
          <div className="summary-search-field">
            <label className="summary-search-label">Unit #</label>
            <input
              className="summary-search-input"
              type="text"
              value={searchFilters.unitId}
              onChange={(event) => updateSearchFilter("unitId", event.target.value)}
              placeholder="Search unit number"
            />
          </div>
          <div className="summary-search-field">
            <label className="summary-search-label">Date From</label>
            <input
              className="summary-search-input"
              type="date"
              value={searchFilters.dateFrom}
              onChange={(event) => updateSearchFilter("dateFrom", event.target.value)}
            />
          </div>
          <div className="summary-search-field">
            <label className="summary-search-label">Date To</label>
            <input
              className="summary-search-input"
              type="date"
              value={searchFilters.dateTo}
              onChange={(event) => updateSearchFilter("dateTo", event.target.value)}
            />
          </div>
        </div>
      </div>

      <div className="summary-controls">
        <span className="sort-label">Sort by</span>
        {[["unit","Unit number"],["date","Date"],["status","Status"]].map(([val, lbl]) => (
          <button
            key={val}
            className={`sort-btn ${sortBy === val ? "active" : ""}`}
            onClick={() => setSortBy(val)}
          >{lbl}</button>
        ))}
        <button
          className="sort-btn"
          onClick={toggleAllFiltered}
          disabled={!hasFilteredEntries}
        >
          {areAllFilteredSelected ? "Deselect All" : "Select All"}
        </button>
        <button
          className="export-btn"
          onClick={() => onExport(Array.from(selected), sortBy)}
          disabled={selected.size === 0 || isExporting}
        >
          Export PDF ↗
        </button>
      </div>

      {filtered.map(u => (
        <div key={u.id} className="summary-unit-group">
          <div className="summary-unit-label-row">
            <input
              type="checkbox"
              className="summary-unit-checkbox"
              checked={selected.has(u.id)}
              onChange={() => toggle(u.id)}
            />
            <span className="summary-unit-text">{u.unitId} — {u.label}</span>
          </div>
          <div className={`summary-entries ${selected.has(u.id) ? "" : "deselected"}`}>
            <div className="summary-entry-row">
              <span className="summary-date">{u.date || "—"}</span>
              <span>{u.deficiencies}</span>
              <StatusPill status={getStatus(u)} />
            </div>
            {u.tasks.map((t, i) => (
              <div key={i} className="summary-task-row">
                <span className="summary-date">{t.tentative || "—"}</span>
                <span className="summary-task-company">
                  {t.company ? `${t.company}: ` : ""}
                  {t.description || "No description"}
                </span>
                <Badge
                  color={isTaskCompletionAchieved(t) ? C.greenLt : C.skyPale}
                  textColor={isTaskCompletionAchieved(t) ? C.green : C.sky}
                >
                  {isTaskCompletionAchieved(t) ? "Done" : "Pending"}
                </Badge>
              </div>
            ))}
          </div>
        </div>
      ))}

      {filtered.length === 0 && (
        <div className="info-banner">
          <span className="info-banner-icon">ℹ</span>
          <span>No summary entries match the current search filters.</span>
        </div>
      )}

      <p className="summary-note">
        Select entries above, then export. Multiple entries appear in a single PDF separated by dividers.
      </p>
    </div>
  );
};

const DelegatePanel = ({
  employees,
  loadingEmployees,
  delegateForm,
  onDelegateFormChange,
  onSendDelegate,
  delegateNotice,
  delegateError,
  isSendingDelegate
}) => {
  const selectedEmployee = employees.find((employee) => employee.id === delegateForm.employeeId) || null;
  const isSendDisabled = !delegateForm.employeeId || !delegateForm.startDate || !delegateForm.endDate || isSendingDelegate;

  return (
    <div className="delegate-container">
      <Divider label="Maintenance log delegate access" color={C.navy} />

      <div className="info-banner delegate-banner">
        <span className="info-banner-icon">ℹ</span>
        <span>
          This prepares the Fleet Manager delegation workflow for a temporary delegate window matching the Maintenance Log module.
        </span>
      </div>

      <div className="delegate-card-grid">
        <div className="delegate-card">
          <div className="section-label">Delegate employee</div>
          <div className="summary-search-field">
            <label className="summary-search-label">Employee</label>
            <select
              className="summary-search-input delegate-select"
              value={delegateForm.employeeId}
              onChange={(event) => onDelegateFormChange("employeeId", event.target.value)}
              disabled={loadingEmployees}
            >
              <option value="">{loadingEmployees ? "Loading employees..." : "Select an employee"}</option>
              {employees.map((employee) => (
                <option key={employee.id} value={employee.id}>
                  {getEmployeeOptionLabel(employee)}
                </option>
              ))}
            </select>
          </div>

          <div className="delegate-preview-block">
            <div className="field-label">Delegate preview</div>
            <div className="delegate-preview-value">
              {selectedEmployee ? getEmployeeOptionLabel(selectedEmployee) : "No delegate selected"}
            </div>
          </div>
        </div>

        <div className="delegate-card">
          <div className="section-label">Delegation window</div>
          <div className="field-grid-2">
            <EditField
              label="Start date"
              value={delegateForm.startDate}
              onChange={(value) => onDelegateFormChange("startDate", value)}
              type="date"
            />
            <EditField
              label="End date"
              value={delegateForm.endDate}
              onChange={(value) => onDelegateFormChange("endDate", value)}
              type="date"
            />
          </div>

          <div className="delegate-preview-block">
            <div className="field-label">Access profile</div>
            <div className="delegate-preview-value">
              Fleet Manager access mirror for steveh@diamondvalley.town during the selected delegate window.
            </div>
          </div>
        </div>
      </div>

      {delegateError && (
        <div className="info-banner delegate-error-banner">
          <span className="info-banner-icon">!</span>
          <span>{delegateError}</span>
        </div>
      )}

      {delegateNotice && (
        <div className="info-banner delegate-success-banner">
          <span className="info-banner-icon">✓</span>
          <span>{delegateNotice}</span>
        </div>
      )}

      <div className="delegate-actions">
        <button
          type="button"
          className="export-btn delegate-send-btn"
          onClick={onSendDelegate}
          disabled={isSendDisabled}
        >
          {isSendingDelegate ? "Sending delegate access..." : "Send delegate access"}
        </button>
      </div>
    </div>
  );
};

// ─── App ──────────────────────────────────────────────────────────────────────
export default function MaintenanceLog() {
  const { user } = useAuth();
  const [units, setUnits] = useState(INITIAL_UNITS);
  const [group, setGroup] = useState("commercial");
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState("");
  const [dirtyUnitIds, setDirtyUnitIds] = useState({});
  const [savingUnitId, setSavingUnitId] = useState(null);
  const [uploadingAttachmentUnitId, setUploadingAttachmentUnitId] = useState(null);
  const [activeAttachmentId, setActiveAttachmentId] = useState(null);
  const [sourceLoadingId, setSourceLoadingId] = useState(null);
  const [notifyingId, setNotifyingId] = useState(null);
  const [isExporting, setIsExporting] = useState(false);
  const [employees, setEmployees] = useState([]);
  const [loadingEmployees, setLoadingEmployees] = useState(false);
  const [delegateForm, setDelegateForm] = useState({
    employeeId: "",
    startDate: "",
    endDate: ""
  });
  const [delegateNotice, setDelegateNotice] = useState("");
  const [delegateError, setDelegateError] = useState("");
  const [isSendingDelegate, setIsSendingDelegate] = useState(false);
  const canManageDelegates = hasMaintenanceLogAccessEmail(getUserEmail(user));

  const buildMaintenanceRequestConfig = (config = {}) => ({
    ...config,
    headers: {
      ...(config.headers || {}),
      ...buildMaintenanceAccessHeaders()
    }
  });

  useEffect(() => {
    const loadMaintenanceLogs = async () => {
      setLoading(true);
      setError("");
      try {
        const response = await API.get("/api/maintenance-logs", {
          ...buildMaintenanceRequestConfig(),
          params: { sortBy: "date" }
        });
        setUnits(Array.isArray(response.data) ? response.data : []);
      } catch (loadError) {
        console.error("Failed to load maintenance logs:", loadError);
        setError("Unable to load maintenance log entries.");
      } finally {
        setLoading(false);
      }
    };

    loadMaintenanceLogs();
  }, []);

  useEffect(() => {
    const loadEmployees = async () => {
      if (!canManageDelegates) {
        setEmployees([]);
        setLoadingEmployees(false);
        return;
      }

      setLoadingEmployees(true);
      try {
        const response = await API.get("/api/users", { params: { role: "EMPLOYEE", scope: "all" } });
        const rawUsers = Array.isArray(response?.data)
          ? response.data
          : Array.isArray(response?.data?.users)
            ? response.data.users
            : [];

        const nextEmployees = rawUsers
          .map(normalizeDirectoryUser)
          .filter((employee) => employee.id)
          .sort((left, right) => left.displayName.localeCompare(right.displayName));

        setEmployees(nextEmployees);
      } catch (employeeError) {
        const fallbackEmployees = Array.from(new Set(
          units
            .map((unit) => String(unit.reporterEmail || "").trim())
            .filter(Boolean)
        )).map((email) => normalizeDirectoryUser({ email }));

        setEmployees(fallbackEmployees);
      } finally {
        setLoadingEmployees(false);
      }
    };

    loadEmployees();
  }, [canManageDelegates, units]);

  const applyServerUnit = (nextUnit) => {
    setUnits((currentUnits) => currentUnits.map((unit) => unit.id === nextUnit.id ? nextUnit : unit));
    setDirtyUnitIds((currentDirtyUnitIds) => {
      const nextDirtyUnitIds = { ...currentDirtyUnitIds };
      delete nextDirtyUnitIds[nextUnit.id];
      return nextDirtyUnitIds;
    });
  };

  const updateUnit = (updated) => {
    setUnits((currentUnits) => currentUnits.map((unit) => unit.id === updated.id ? updated : unit));
    setDirtyUnitIds((currentDirtyUnitIds) => ({
      ...currentDirtyUnitIds,
      [updated.id]: true
    }));
  };

  const saveUnit = async (updated) => {
    setSavingUnitId(updated.id);
    setError("");
    try {
      const response = await API.patch(`/api/maintenance-logs/${updated.id}`, {
        group: updated.group,
        date: updated.date,
        unitId: updated.unitId,
        label: updated.label,
        deficiencies: updated.deficiencies,
        notes: updated.notes,
        tasks: updated.tasks,
        attachments: updated.attachments,
        reporter: updated.reporter,
        reporterEmail: updated.reporterEmail,
        sourceLink: updated.sourceLink,
        status: updated.status,
        completionDate: updated.completionDate,
        hasActiveDeficiencies: updated.hasActiveDeficiencies,
        department: updated.department,
        vehicleType: updated.vehicleType,
        vehicleDescription: updated.vehicleDescription,
        plate: updated.plate
      }, buildMaintenanceRequestConfig());
      applyServerUnit(response.data);
    } catch (saveError) {
      console.error("Failed to save maintenance log entry:", saveError);
      setError("Unable to save maintenance log changes.");
    } finally {
      setSavingUnitId(null);
    }
  };

  const uploadAttachments = async (unit, files) => {
    setUploadingAttachmentUnitId(unit.id);
    setError("");

    try {
      let latestUnit = unit;

      for (const file of files) {
        const formData = new FormData();
        formData.append("file", file);
        formData.append("entrySnapshot", JSON.stringify(buildEntrySnapshot(latestUnit)));

        const response = await API.post(`/api/maintenance-logs/${unit.id}/attachments`, formData, buildMaintenanceRequestConfig());
        latestUnit = response.data;
      }

      applyServerUnit(latestUnit);
    } catch (uploadError) {
      console.error("Failed to upload maintenance attachment:", uploadError);
      const message = uploadError?.response?.data?.error || uploadError?.response?.data?.message || "Unable to upload maintenance attachment.";
      setError(message);
    } finally {
      setUploadingAttachmentUnitId(null);
    }
  };

  const removeAttachment = async (unit, attachment, attachmentIndex) => {
    if (!attachment || typeof attachment === "string" || !attachment.id) {
      const nextUnit = {
        ...unit,
        attachments: unit.attachments.filter((_, index) => index !== attachmentIndex)
      };

      setUnits((currentUnits) => currentUnits.map((currentUnit) => currentUnit.id === unit.id ? nextUnit : currentUnit));
      await saveUnit(nextUnit);
      return;
    }

    const attachmentId = getAttachmentId(attachment);
    setActiveAttachmentId(attachmentId);
    setError("");

    try {
      const response = await API.delete(`/api/maintenance-logs/${unit.id}/attachments/${attachmentId}`, {
        ...buildMaintenanceRequestConfig(),
        data: {
          entrySnapshot: buildEntrySnapshot(unit)
        }
      });

      applyServerUnit(response.data);
    } catch (removeError) {
      console.error("Failed to remove maintenance attachment:", removeError);
      const message = removeError?.response?.data?.error || "Unable to remove maintenance attachment.";
      setError(message);
    } finally {
      setActiveAttachmentId(null);
    }
  };

  const openSourceReport = async (unit) => {
    if (!unit?.sourceLink) {
      return;
    }

    setSourceLoadingId(unit.id);
    try {
      if (!String(unit.sourceLink).startsWith("/api/")) {
        const popup = window.open(unit.sourceLink, "_blank", "noopener,noreferrer");
        if (!popup) {
          alert("Popup blocked. Allow popups for this site to open the original inspection form.");
        }
        return;
      }

      const response = await API.get(unit.sourceLink, { responseType: "blob" });
      const blobUrl = window.URL.createObjectURL(new Blob([response.data], { type: "application/pdf" }));
      const popup = window.open(blobUrl, "_blank", "noopener,noreferrer");
      if (!popup) {
        alert("Popup blocked. Allow popups for this site to view the PDF in a new tab.");
      }
    } catch (openError) {
      console.error("Failed to open source report:", openError);
      setError("Unable to open the original inspection form.");
    } finally {
      setSourceLoadingId(null);
    }
  };

  const openAttachment = async (unit, attachment) => {
    const attachmentId = getAttachmentId(attachment);
    const downloadUrl = getAttachmentDownloadUrl(unit, attachment);

    if (!downloadUrl) {
      return;
    }

    setActiveAttachmentId(attachmentId);
    try {
      const response = await API.get(downloadUrl, {
        responseType: "blob"
      });
      const blobType = attachment?.mimeType || attachment?.type || response.data?.type || "application/octet-stream";
      const blobUrl = window.URL.createObjectURL(new Blob([response.data], { type: blobType }));
      const popup = window.open(blobUrl, "_blank", "noopener,noreferrer");
      if (!popup) {
        alert("Popup blocked. Allow popups for this site to view the attachment in a new tab.");
      }
    } catch (openError) {
      console.error("Failed to open maintenance attachment:", openError);
      setError("Unable to open the attachment.");
    } finally {
      setActiveAttachmentId(null);
    }
  };

  const notifyEmployee = async (unit) => {
    setNotifyingId(unit.id);
    try {
      const response = await API.post(`/api/maintenance-logs/${unit.id}/notify-employee`, {}, buildMaintenanceRequestConfig());
      if (response.data?.entry) {
        applyServerUnit(response.data.entry);
      }
    } catch (notifyError) {
      console.error("Failed to notify employee:", notifyError);
      const message = notifyError?.response?.data?.error || "Unable to send maintenance summary to the employee.";
      setError(message);
    } finally {
      setNotifyingId(null);
    }
  };

  const exportSummary = async (selectedIds, sortBy) => {
    if (!Array.isArray(selectedIds) || selectedIds.length === 0) {
      return;
    }

    setIsExporting(true);
    try {
      const response = await API.get("/api/maintenance-logs/pdf/summary", {
        ...buildMaintenanceRequestConfig(),
        params: {
          ids: selectedIds.join(","),
          sortBy
        },
        responseType: "blob"
      });

      const blobUrl = window.URL.createObjectURL(new Blob([response.data], { type: "application/pdf" }));
      const popup = window.open(blobUrl, "_blank", "noopener,noreferrer");
      if (!popup) {
        alert("Popup blocked. Allow popups for this site to view the PDF in a new tab.");
      }
    } catch (exportError) {
      console.error("Failed to export maintenance summary PDF:", exportError);
      setError("Unable to export the maintenance log summary PDF.");
    } finally {
      setIsExporting(false);
    }
  };

  const updateDelegateForm = (field, value) => {
    setDelegateForm((currentForm) => ({
      ...currentForm,
      [field]: value
    }));
    setDelegateNotice("");
    setDelegateError("");
  };

  const sendDelegateAccess = async () => {
    const selectedEmployee = employees.find((employee) => employee.id === delegateForm.employeeId);

    if (!selectedEmployee || !selectedEmployee.email) {
      setDelegateError("Select an employee to receive delegated Maintenance Log access.");
      return;
    }

    if (!delegateForm.startDate || !delegateForm.endDate) {
      setDelegateError("Select both the start date and end date for the delegation window.");
      return;
    }

    if (delegateForm.endDate < delegateForm.startDate) {
      setDelegateError("The delegation end date must be on or after the start date.");
      return;
    }

    setIsSendingDelegate(true);
    setDelegateError("");

    try {
      const response = await API.post("/api/maintenance-logs/delegations", {
        delegateEmail: selectedEmployee.email,
        delegateName: selectedEmployee.displayName,
        startDate: delegateForm.startDate,
        endDate: delegateForm.endDate
      }, buildMaintenanceRequestConfig());

      const delegation = response.data?.delegation || {};
      setDelegateNotice(`Delegate access email sent to ${selectedEmployee.displayName} for ${delegation.startDate || delegateForm.startDate} through ${delegation.endDate || delegateForm.endDate}. The delegate link only works during that window.`);
    } catch (delegateSendError) {
      console.error("Failed to send maintenance delegate access:", delegateSendError);
      const message = delegateSendError?.response?.data?.error || "Unable to send delegate access for the Maintenance Log.";
      setDelegateError(message);
    } finally {
      setIsSendingDelegate(false);
    }
  };

  const visibleUnits = units.filter((unit) => {
    if (unit.group !== group) {
      return false;
    }

    if (dirtyUnitIds[unit.id]) {
      return true;
    }

    return deriveUnitStatus(unit) !== "Completed";
  });

  const tabs = [
    { id: "commercial",    label: "Commercial Vehicles"   },
    { id: "noncommercial", label: "Non-Commercial Equip." },
    { id: "summary",       label: "Summary & Export"      },
    ...(canManageDelegates ? [{ id: "delegate", label: "Delegate" }] : []),
  ];

  return (
    <div className="app-root">
      {/* Top bar */}
      <div className="topbar">
        <div className="topbar-inner">
          <div className="topbar-header">
            <div>
              <h1 className="topbar-title">Vehicle &amp; Equipment Maintenance Log</h1>
            </div>
          </div>

          {/* Group navigation */}
          <div className="group-tabs">
            {tabs.map(t => (
              <button
                key={t.id}
                className={`group-tab ${group === t.id ? "active" : ""}`}
                onClick={() => setGroup(t.id)}
              >{t.label}</button>
            ))}
          </div>
        </div>
      </div>

      {/* Content */}
      <div className="content">
        {error && (
          <div className="info-banner">
            <span className="info-banner-icon">!</span>
            <span>{error}</span>
          </div>
        )}

        {group !== "summary" && (
          <div className="info-banner">
            <span className="info-banner-icon">ℹ</span>
            <span>
              Deficiencies auto-populate from pre-job equipment checks and vehicle/equipment
              inspection reports. Fleet Manager or delegate can update the green tab fields.
            </span>
          </div>
        )}

        {group === "summary" ? (
          <div className="summary-container">
            <Divider label="Maintenance / repair log summary" color={C.navy} />
            <SummaryPanel units={units} onExport={exportSummary} isExporting={isExporting} />
          </div>
        ) : group === "delegate" ? (
          <div className="summary-container">
            <DelegatePanel
              employees={employees}
              loadingEmployees={loadingEmployees}
              delegateForm={delegateForm}
              onDelegateFormChange={updateDelegateForm}
              onSendDelegate={sendDelegateAccess}
              delegateNotice={delegateNotice}
              delegateError={delegateError}
              isSendingDelegate={isSendingDelegate}
            />
          </div>
        ) : loading ? (
          <div className="info-banner">
            <span className="info-banner-icon">…</span>
            <span>Loading maintenance log entries.</span>
          </div>
        ) : visibleUnits.length === 0 ? (
          <div className="info-banner">
            <span className="info-banner-icon">ℹ</span>
            <span>No maintenance log entries found for this group.</span>
          </div>
        ) : (
          visibleUnits.map(u => (
            <UnitRow
              key={u.id}
              unit={u}
              onUpdate={updateUnit}
              onSave={saveUnit}
              onUploadAttachments={uploadAttachments}
              onRemoveAttachment={removeAttachment}
              onOpenAttachment={openAttachment}
              hasUnsavedChanges={Boolean(dirtyUnitIds[u.id])}
              onOpenSource={openSourceReport}
              onNotify={notifyEmployee}
              savingUnitId={savingUnitId}
              uploadingAttachmentUnitId={uploadingAttachmentUnitId}
              activeAttachmentId={activeAttachmentId}
              sourceLoadingId={sourceLoadingId}
              notifyingId={notifyingId}
            />
          ))
        )}
      </div>
    </div>
  );
}